import { useQuery } from '@tanstack/react-query'
import { GroupNote } from './GroupNote'
import { Panel } from '../../../components/Panel'
import { DeskFrame } from './DeskFrame'
import { NotYetLive } from './NotYetLive'
import { useDeskState } from './useDeskState'
import { deskApi, type DeskPub, type Meta, type SourceRow } from '../../../api/visitorsDesk'
import { FILTER_KEYS, TAB_FILTERS, filterLabel, type DeskFilter } from '../../../lib/deskFilters'
import { CREDIT_TODAY_NOTE, compareHeader, creditText, fmtDay, pubLabel, rateWithCount } from '../../../lib/deskFormat'
import { changePctText, drillCrumbs, rowFilters, sourceTree } from '../../../lib/deskSources'
import { fmtDate, fmtInt } from '../../../lib/format'

const TONE = { up: 'text-radar-cyan', down: 'text-amber-glow', flat: 'text-paper-faint' } as const
const th = 'px-2 py-1.5 text-left font-mono text-[11px] font-semibold uppercase tracking-wide text-paper-faint'
const td = 'px-2 py-1.5 align-top'
const mono = { fontVariantNumeric: 'tabular-nums' } as const

function Loading() { return <div className="h-24 bg-ink-800 animate-pulse" /> }
function Failed({ what }: { what: string }) { return <p className="font-mono text-[12px] text-radar-red">Couldn’t load {what}</p> }
/** Grey tag for a panel the referrer pill does not re-query (only the drill does).
 *  Channel pills, which no Sources panel applies, are tagged once by the frame. */
function NotFiltered({ filters }: { filters: DeskFilter[] }) {
  const set = filters.filter((f) => TAB_FILTERS.sources.includes(f.key))
  if (set.length === 0) return null
  return (
    <div className="flex flex-wrap gap-2">
      {set.map((f) => <span key={f.key} className="border border-ink-500 px-1.5 py-0.5 font-mono text-[11px] text-paper-faint">Not filtered by {filterLabel(f)}</span>)}
    </div>
  )
}
const PER_DAY = 'People are counted once per day per source'
function Notes({ meta }: { meta: Meta | undefined }) {
  const notes = (meta?.notes ?? []).filter((n) => !n.startsWith('People are counted once per day'))
  if (notes.length === 0) return null
  return <ul className="space-y-0.5 font-mono text-[12px] text-paper-faint">{notes.map((n) => <li key={n}>{n}</li>)}</ul>
}

export function DeskSources() {
  return <DeskFrame tab="sources"><Body /></DeskFrame>
}

function Body() {
  const { pub, q, filters, spKey, setSp } = useDeskState('sources')
  const p = pub as DeskPub
  const key = [pub, q.period, q.from, q.to, q.compare, q.group]
  const host = filters.find((f) => f.key === 'referrer_host')?.value
  const table = useQuery({ queryKey: ['desk', 'sources', ...key], queryFn: () => deskApi.sources(p, q), retry: false })

  const setFilter = (fs: DeskFilter[]) => {
    const next = new URLSearchParams(spKey)
    for (const k of ['channel', 'channel_detail', 'referrer_host'] as const) next.delete(`f.${k}`)
    for (const f of fs) next.set(`f.${f.key}`, f.value)
    setSp(next)
  }
  const dropFilters = (keys: readonly string[]) => {
    const next = new URLSearchParams(spKey)
    for (const k of keys) if ((FILTER_KEYS as readonly string[]).includes(k)) next.delete(`f.${k}`)
    setSp(next)
  }

  if (table.isError) return <Failed what="the sources" />
  if (!table.data) return <Loading />
  if (table.data.unavailable !== undefined) return <NotYetLive text={table.data.unavailable} />
  const d = table.data
  const live = q.period === 'today'
  return (
    <div className="space-y-4">
      <SourceTable rows={d.rows} directNote={d.direct_note} meta={d.meta} live={live} vs={compareHeader(q.compare)} filters={filters} onPick={setFilter} />
      {host && <Referrers p={p} q={q} host={host} filters={filters} rows={d.rows} onCrumb={dropFilters} pub={pub} />}
      <div className="grid gap-4 grid-cols-1 xl:grid-cols-2">
        <NewSources p={p} q={q} filters={filters} />
        <Email p={p} q={q} pub={pub} filters={filters} />
      </div>
      <Social p={p} q={q} filters={filters} onPick={setFilter} />
    </div>
  )
}

type Q = ReturnType<typeof useDeskState>['q']

function SourceTable({ rows, directNote, meta, live, vs, filters, onPick }: {
  rows: SourceRow[]; directNote: string; meta: Meta; live: boolean; vs: string; filters: DeskFilter[]; onPick: (fs: DeskFilter[]) => void
}) {
  const tree = sourceTree(rows)
  const max = Math.max(1, ...rows.filter((r) => r.level === 'channel').map((r) => r.people))
  const picked = (r: SourceRow) => rowFilters(r).every((x) => filters.some((f) => f.key === x.key && f.value === x.value))
  const line = (r: SourceRow) => {
    const ch = changePctText(r.change_pct)
    const top = r.level === 'channel'
    return (
      <tr key={r.key} className={`border-t border-ink-600 ${top ? 'bg-ink-800 font-semibold' : ''} ${picked(r) ? 'bg-ink-700' : ''}`}>
        <td className={`${td} ${top ? '' : 'pl-6'}`}>
          <button type="button" title={r.label} aria-pressed={picked(r)} onClick={() => onPick(rowFilters(r))}
                  className="block max-w-[260px] truncate text-left text-amber-glow hover:underline">{r.label}</button>
        </td>
        <td className={`${td} text-right font-mono`} style={mono}>{fmtInt(r.people)}</td>
        <td className={td}>
          <div className="flex items-center gap-2" title={r.share === null ? 'Share hidden: fewer than 30 people overall' : undefined}>
            <div className="h-2 min-w-[60px] flex-1 bg-ink-700" aria-hidden="true"><div className="h-2 bg-amber-glow" style={{ width: r.share === null ? 0 : `${Math.round((r.people / max) * 100)}%` }} /></div>
            <span className="w-10 text-right font-mono text-[12px] text-paper-faint" style={mono}>{r.share === null ? '—' : `${Math.round(r.share * 100)}%`}</span>
          </div>
        </td>
        <td className={`${td} text-right font-mono ${TONE[ch.tone]}`} style={mono}>{ch.text}</td>
        <td className={`${td} text-right font-mono`} style={mono} title={`of ${fmtInt(r.visits)} visits`}>{r.engaged_rate === null ? '—' : rateWithCount(r.engaged_rate, r.visits)}</td>
        <td className={`${td} text-right font-mono`} style={mono}>{r.pages_per_visit === null ? '—' : r.pages_per_visit.toFixed(1)}</td>
        <td className={`${td} text-right font-mono`} style={mono}>{fmtInt(r.became_regular)}</td>
        <td className={`${td} text-right font-mono`} style={mono}>{fmtInt(r.signed_up)}</td>
        <td className={`${td} text-right font-mono`} style={mono} title={live ? 'credited overnight' : undefined}>{creditText(r.members_gained, live)}</td>
      </tr>
    )
  }
  return (
    <Panel label="Where people came from" accent="amber" right={<span className="font-mono text-[11px] text-paper-faint">Click a row to pick it out (See the visits → opens them); click a site to see the exact pages that linked to us. The last three columns show what each source turned into.</span>}>
      <div className="space-y-2">
        <NotFiltered filters={filters} />
        <div className="overflow-x-auto">
          <table className="w-full min-w-[900px] text-[13px]">
            <thead>
              <tr>
                <th className={th}>Channel / source</th>
                <th className={`${th} text-right`}>People</th>
                <th className={th}>Share</th>
                {[vs, 'Engaged', 'Pages / visit', 'Became regular', 'Signed up (confirmed)'].map((h) => <th key={h} className={`${th} text-right`}>{h}</th>)}
                <th className={`${th} text-right`}>Members gained</th>
              </tr>
            </thead>
            <tbody>
              {tree.flatMap((n) => [line(n.row), ...n.children.map(line)])}
              {rows.length === 0 && <tr><td colSpan={9} className={`${td} text-paper-faint`}>No visits in this period</td></tr>}
            </tbody>
          </table>
        </div>
        <p className="text-[12px] text-paper-faint">{PER_DAY}.</p>
        {live && <p className="font-mono text-[12px] text-paper-faint">{CREDIT_TODAY_NOTE}</p>}
        <p className="text-[12px] text-paper-faint">{directNote}</p>
        <Notes meta={meta} />
      </div>
    </Panel>
  )
}

function Referrers({ p, q, host, filters, rows, onCrumb, pub }: {
  p: DeskPub; q: Q; host: string; filters: DeskFilter[]; rows: SourceRow[]; onCrumb: (keys: readonly string[]) => void; pub: string
}) {
  const query = useQuery({ queryKey: ['desk', 'sources-ref', pub, q.period, q.from, q.to, q.compare, q.group, host], queryFn: () => deskApi.sourceReferrers(p, q, host), retry: false })
  const labels = Object.fromEntries(rows.filter((r) => r.level === 'channel').map((r) => [r.key, r.label]))
  const crumbs = drillCrumbs(filters, labels)
  const d = query.data
  return (
    <Panel label="Exactly which pages sent them" accent="cyan">
      <div className="space-y-2">
        <nav className="flex flex-wrap items-center gap-1.5 text-[13px]" aria-label="Referrer trail">
          {crumbs.map((c) => (
            <span key={c.label} className="flex items-center gap-1.5">
              {c.remove.length > 0
                ? <button type="button" onClick={() => onCrumb(c.remove)} className="text-amber-glow hover:underline">{c.label}</button>
                : <span className="text-paper">{c.label}</span>}
              <span className="text-paper-faint">›</span>
            </span>
          ))}
          <strong className="text-paper">linking pages</strong>
        </nav>
        {query.isError ? <Failed what="the linking pages" /> : !d ? <Loading /> : d.unavailable !== undefined ? <p className="text-paper">{d.unavailable}</p> : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full min-w-[520px] text-[13px]">
                <thead><tr><th className={th}>Referring page</th><th className={th}>Landed on</th><th className={`${th} text-right`}>People</th><th className={`${th} text-right`}>Regular / joined</th></tr></thead>
                <tbody>
                  {d.rows.map((r) => (
                    <tr key={`${r.referrer_url}-${r.landing}`} className="border-t border-ink-600">
                      <td className={td}><span className="block max-w-[320px] truncate" title={r.referrer_url}>{r.label}</span></td>
                      <td className={td}><span className="block max-w-[220px] truncate" title={r.landing}>{r.landing}</span></td>
                      <td className={`${td} text-right font-mono`} style={mono}>{fmtInt(r.people)}</td>
                      <td className={`${td} text-right font-mono`} style={mono}>{fmtInt(r.became_regular)} / {fmtInt(r.joined)}</td>
                    </tr>
                  ))}
                  {d.rows.length === 0 && <tr><td colSpan={4} className={`${td} text-paper-faint`}>No linking pages in this period</td></tr>}
                </tbody>
              </table>
            </div>
            <p className="text-[12px] text-paper-faint">Full referring address is kept. Google, Facebook and apps usually send only the site name, and those rows say so.</p>
            <Notes meta={d.meta} />
          </>
        )}
      </div>
    </Panel>
  )
}

function NewSources({ p, q, filters }: { p: DeskPub; q: Q; filters: DeskFilter[] }) {
  const query = useQuery({ queryKey: ['desk', 'sources-new', p, q.period, q.from, q.to, q.compare, q.group], queryFn: () => deskApi.newSources(p, q), retry: false })
  const d = query.data
  return (
    <Panel label="New sources · not seen in the last 90 days" accent="violet">
      {query.isError ? <Failed what="new sources" /> : !d ? <Loading /> : d.unavailable !== undefined ? <p className="text-paper">{d.unavailable}</p> : (
        <div className="space-y-2">
          <NotFiltered filters={filters} />
          <div className="overflow-x-auto">
            <table className="w-full min-w-[420px] text-[13px]">
              <thead><tr><th className={th}>Site</th><th className={th}>First seen</th><th className={`${th} text-right`}>People</th><th className={th}>Top landing page</th></tr></thead>
              <tbody>
                {d.rows.map((r) => (
                  <tr key={r.host} className="border-t border-ink-600">
                    <td className={`${td} font-semibold`}><span className="block max-w-[200px] truncate" title={r.host}>{r.host}</span></td>
                    <td className={`${td} text-paper-faint`}>{fmtDay(r.first_seen, { month: 'short', day: 'numeric' })}</td>
                    <td className={`${td} text-right font-mono`} style={mono}>{fmtInt(r.people)}</td>
                    <td className={td}><span className="block max-w-[220px] truncate" title={r.top_landing ?? ''}>{r.top_landing ?? '—'}</span></td>
                  </tr>
                ))}
                {d.rows.length === 0 && <tr><td colSpan={4} className={`${td} text-paper-faint`}>{d.collecting ? 'Nothing to show yet' : 'No new sites sent anyone in this period'}</td></tr>}
              </tbody>
            </table>
          </div>
          {d.collecting && <p className="font-mono text-[12px] text-paper-faint">Collecting: {d.history_days} of 90 days of history so far, so a site may look new only because we have not watched long enough.</p>}
          <p className="text-[12px] text-paper-faint">You get an alert when a new site sends 10+ people. Someone is talking about us there: join the thread, thank the club, or pitch the forum.</p>
          <Notes meta={d.meta} />
        </div>
      )}
    </Panel>
  )
}

function Email({ p, q, pub, filters }: { p: DeskPub; q: Q; pub: string; filters: DeskFilter[] }) {
  const live = q.period === 'today'
  const query = useQuery({ queryKey: ['desk', 'sources-email', p, q.period, q.from, q.to, q.compare, q.group], queryFn: () => deskApi.sourceEmails(p, q), enabled: !live, retry: false })
  const d = query.data
  return (
    <Panel label="Email & newsletters" accent="green">
      {live ? <p className="text-paper-dim">Email sends are read for a finished period. Pick yesterday or a longer period.</p>
        : query.isError ? <Failed what="email sends" /> : !d ? <Loading /> : d.unavailable !== undefined ? <p className="text-paper">{d.unavailable}</p> : (
          <div className="space-y-2">
            <GroupNote applied={d.group_applied} />
            <NotFiltered filters={filters} />
            <div className="overflow-x-auto">
              <table className="w-full min-w-[460px] text-[13px]">
                <thead><tr><th className={th}>Send</th><th className={`${th} text-right`}>Opened</th><th className={`${th} text-right`}>Visits</th><th className={`${th} text-right`}>vs usual</th><th className={`${th} text-right`}>Members from it</th></tr></thead>
                <tbody>
                  {d.rows.map((r) => {
                    const ch = changePctText(r.vs_usual_pct)
                    return (
                      <tr key={`${r.publication}-${r.label}-${r.sent_at}`} className="border-t border-ink-600">
                        <td className={td}>
                          <span className="block max-w-[220px] truncate" title={r.label}>{r.label}</span>
                          <span className="font-mono text-[11px] text-paper-faint">{fmtDate(r.sent_at, { month: 'short', day: 'numeric' })}{pub === 'all' ? ` · ${pubLabel(r.publication)}` : ''} · {fmtInt(r.sent)} sent</span>
                        </td>
                        <td className={`${td} text-right font-mono`} style={mono}>{r.opened_rate === null ? '—' : `${Math.round(r.opened_rate * 100)}%`}</td>
                        <td className={`${td} text-right font-mono`} style={mono}>{fmtInt(r.visits)}</td>
                        <td className={`${td} text-right font-mono ${TONE[ch.tone]}`} style={mono}>{ch.text}</td>
                        <td className={`${td} text-right font-mono`} style={mono}>{fmtInt(r.paid)}</td>
                      </tr>
                    )
                  })}
                  {d.rows.length === 0 && <tr><td colSpan={5} className={`${td} text-paper-faint`}>No sends in this period</td></tr>}
                </tbody>
              </table>
            </div>
            <p className="text-[12px] text-paper-faint">"vs usual" compares each send with that title's last 8 sends of the same kind.</p>
            <Notes meta={d.meta} />
          </div>
        )}
    </Panel>
  )
}

function Social({ p, q, filters, onPick }: { p: DeskPub; q: Q; filters: DeskFilter[]; onPick: (fs: DeskFilter[]) => void }) {
  const query = useQuery({ queryKey: ['desk', 'sources-social', p, q.period, q.from, q.to, q.compare, q.group], queryFn: () => deskApi.sourceSocial(p, q), retry: false })
  const d = query.data
  return (
    <Panel label="Social & AI assistants, by network" accent="none">
      {query.isError ? <Failed what="social networks" /> : !d ? <Loading /> : d.unavailable !== undefined ? <p className="text-paper">{d.unavailable}</p> : (
        <div className="space-y-2">
          <NotFiltered filters={filters} />
          <div className="overflow-x-auto">
            <table className="w-full min-w-[460px] text-[13px]">
              <thead><tr><th className={th}>Network</th><th className={`${th} text-right`}>People</th><th className={`${th} text-right`}>{compareHeader(q.compare)}</th><th className={`${th} text-right`}>Engaged</th><th className={th}>Top post / page</th></tr></thead>
              <tbody>
                {d.rows.map((r) => {
                  const ch = changePctText(r.change_pct)
                  return (
                    <tr key={`${r.channel}-${r.network}`} className="border-t border-ink-600">
                      <td className={td}>
                        <button type="button" title={r.network} onClick={() => onPick([{ key: 'channel_detail', value: r.network }])}
                                className="block max-w-[200px] truncate text-left text-amber-glow hover:underline">{r.network}</button>
                      </td>
                      <td className={`${td} text-right font-mono`} style={mono}>{fmtInt(r.people)}</td>
                      <td className={`${td} text-right font-mono ${TONE[ch.tone]}`} style={mono}>{ch.text}</td>
                      <td className={`${td} text-right font-mono`} style={mono} title={`of ${fmtInt(r.visits)} visits`}>{r.engaged_rate === null ? '—' : rateWithCount(r.engaged_rate, r.visits)}</td>
                      <td className={td}><span className="block max-w-[240px] truncate" title={r.top_landing ?? ''}>{r.top_landing ?? '—'}</span></td>
                    </tr>
                  )
                })}
                {d.rows.length === 0 && <tr><td colSpan={5} className={`${td} text-paper-faint`}>No social or assistant visits in this period</td></tr>}
              </tbody>
            </table>
          </div>
          <Notes meta={d.meta} />
        </div>
      )}
    </Panel>
  )
}
